import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { API_URL } from 'constant';
import { loginState } from 'global-stores';
import { useNetwork } from 'hooks';

import { DashboardsAtom } from './states';

/** Local-timezone YYYY-MM-DD; toISOString() would shift the day for UTC-N. */
const isoDay = (d: Date) =>
	`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
		d.getDate()
	).padStart(2, '0')}`;

/**
 * /onboarding-summary -> DashboardsAtom.
 *
 * This atom is NOT dashboard-private: hooks/allowed-invites, views/pipelines and
 * settings/billing all read it, which is why routes-children primes it once at
 * app mount. The Ops Deck reads the primed value and only calls this when the
 * user moves off that default range.
 *
 * getAnalyticsData / getOnboardingFlowAnalyticsData used to live here and both
 * called `/analytics?business=<id>` — the same endpoint, once with an ignored
 * `graph=true` flag — writing the identical response into two atoms on every
 * mount and every date change. The deck makes that call once, itself.
 * getCountries / getUserLocations went with the choropleth.
 */
export const useDashboards = () => {
	const userLogin = useRecoilValue(loginState);
	const setDashboards = useSetRecoilState(DashboardsAtom);
	const { get } = useNetwork({ updateState: false });

	const getDashboards = useCallback(
		(startDate?: Date, endDate?: Date) => {
			setDashboards(prev => ({ ...prev, loaded: false }));
			const range =
				startDate && endDate
					? `&startDate=${isoDay(startDate)}&endDate=${isoDay(endDate)}`
					: '';
			get(
				`${API_URL.ONBOARDED_SUMMARY}?timeZoneOffset=${new Date().getTimezoneOffset()}${range}`
			)
				.then(resp =>
					setDashboards(prev =>
						resp?.data
							? { ...prev, data: resp.data, loaded: true }
							: { ...prev, loaded: true }
					)
				)
				.catch(() =>
					setDashboards(prev => ({ ...prev, error: true, loaded: true }))
				);
		},
		[get, setDashboards]
	);

	return { getDashboards, business: userLogin?.business };
};

/** Kept for components/country-dropdown, which imports the shape from here. */
export interface Country {
	name: string;
	isoCode: string;
	mapKey: string;
}
